import { ServiceProvider, StorageItem } from '@uploadcam/common';
import { GetCredentialInteractor } from '../../../auth/domain/interactors/get-credential.interactor';
import { DROPBOX_REAL_ROOT } from '../../data/dropbox/dropbox.adapter';
import { StorageDialogService } from '../../storage-dialog.service';
import { StorageProvider } from '../../storage.provider';

/**
 * Opens the folder browser in multi-select mode so the user can pick several
 * folders at once (used by the "import folders from a cloud provider" flow).
 *
 * Unlike {@link ShowFolderPickerInteractor} this always uses the in-app browser
 * and never falls back to the native Google Picker: the caller gates the entry
 * point behind the full Drive scope, which the browser requires to list
 * arbitrary folders.
 */
export class ShowMultiFolderPickerInteractor {
  constructor(
    private readonly getCredential: GetCredentialInteractor,
    private readonly storageDialog: StorageDialogService,
    private readonly storageProvider: StorageProvider,
  ) {}

  public async execute(title: string, foldersBlacklist: string[]): Promise<StorageItem[]> {
    const manager = await this.storageProvider.provideManager();
    const root = await manager.getRootFolder();

    // On a Dropbox team account the Dropbox root lists the team's "Team folders",
    // which are namespace roots rather than importable folders (the Dropbox
    // equivalent of Google Shared Drives). They must be navigable but not
    // selectable, so disable every direct child of the Dropbox real root.
    const isDropboxTeam = await this.isDropboxTeam();

    const selected = await this.storageDialog.openSelectFoldersDialog({
      title,
      config: {
        root,
        canBrowse: () => true,
        isDisabled: (currentFolder, item) => {
          if (isDropboxTeam && currentFolder.id === DROPBOX_REAL_ROOT) {
            return {
              state: true,
              reason: 'Dropbox Team folders cannot be imported. Open one to import the folders inside it.',
            };
          }

          return {
            state: foldersBlacklist.includes(item.id),
            reason: 'This folder is already a Team Folder',
          };
        },
        listFolder: (item, onlyFolders) => manager.listFolder(item, onlyFolders),
        // Not used in multi-select mode, but required by the config contract.
        setSelected: (selected) => selected,
        showCreateFolder: () => false,
      },
    });

    return (selected ?? []).filter(
      (item) => !item.isVirtual && !foldersBlacklist.includes(item.id),
    );
  }

  private async isDropboxTeam(): Promise<boolean> {
    const credential = this.getCredential.execute();

    if (credential.provider !== ServiceProvider.Dropbox) {
      return false;
    }

    const adapter = await this.storageProvider.provideDropboxAdapter(credential);

    return adapter.isTeam;
  }
}
